import { ExpandAltOutlined, ReloadOutlined, ShrinkOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Button, Collapse, Empty, Select, Spin, Typography } from 'antd'
import { useAtom } from 'jotai'
import { flatMapDeep, keyBy, round } from 'lodash'
import queryString from 'query-string'
import React from 'react'
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'

import {
  DepartmentName,
  NoPermissionPage,
  RelationsCreatorDropdown,
  UserAvatar,
  getPointPermission,
} from '@/components'
import { ResourceUsers } from '@/components/resouce-users/resource-users.component'
import { currentOKRCycleAtom, okrCyclesAtom } from '@/modules/okr'
import { NormalResourceTable, deleteChildren } from '@/modules/portfolio/portfolio-list'
import {
  PAGE_RESOURCE_POINTS_ENUM,
  PpRelation as PpRelationBase,
  PpRelationObjective,
  ResourceType,
  ResourceUser,
  Team,
} from '@/schemas'

const { Text } = Typography

type PpRelation = PpRelationBase | PpRelationObjective

interface TeamOKRProps {
  team: Team
  departmentId?: number
  setProgress: React.Dispatch<React.SetStateAction<number>>
  setUsers?: React.Dispatch<React.SetStateAction<ResourceUser[] | undefined>>
}

interface TeamOKRChildrenProps {
  selfId: string
  type: ResourceType.PROJECT | ResourceType.PORTFOLIO | ResourceType.OBJECTIVE
  nodes?: PpRelation[]
  refresh: () => void
  canUpdate: boolean
  canDelete: boolean
  depth: number
  activeKeys: string[]
  onKeyChange: (keys: string | string[]) => void
}

const reorder = (res: PpRelation[], sourceIndex: number, targetIndex: number) => {
  // const res = Array.from(list)
  const [item] = res.splice(sourceIndex, 1)
  res.splice(targetIndex, 0, item)
  return res.map((r, index) => ({
    id: r.id,
    order: index,
  }))
}

const flatten = (data: any) => {
  let arr = new Array<any>()
  for (let i = 0; i < data.length; i++) {
    if (data[i].children && data[i].children.length) {
      arr = arr.concat(flatten(data[i].children))
    }
    arr.push(data[i])
  }
  return arr
}

const TeamOKRChildren: React.FC<TeamOKRChildrenProps> = ({
  selfId,
  type,
  nodes = [],
  refresh,
  canDelete,
  canUpdate,
  depth,
  activeKeys,
  onKeyChange,
}) => {
  const { t } = useTranslation()
  const childObjectives = (nodes.filter((c) => c.type === 2) as PpRelationObjective[]).map((node, index) => {
    const { objective, children = [] } = node
    const header = (
      <div className='flex items-center group'>
        <div className='flex mr-12px'>
          <UserAvatar email={objective.email} />
        </div>
        <div>
          <a
            target='_blank'
            rel='noreferrer'
            href={`https://okr.agoralab.co/okr/overview?cycle=${objective.cycleId}&selectKey=${objective.departmentId}`}
          >
            <div className='font-bold'>{objective!.name}</div>
          </a>
          <Text type='secondary' className='text-xs children:mr-8px'>
            <span>
              <DepartmentName canRead={true} departmentId={objective.departmentId} />
              <span className='ml-5px inline-block'>
                {t('progress')} {round(objective.progress, 0)}%
              </span>
            </span>
            {canUpdate && (
              <RelationsCreatorDropdown
                direction='up'
                rootSelfId={selfId}
                selfId={objective.objectiveId}
                type={ResourceType.OBJECTIVE}
                onSuccess={refresh}
                disabledSelfIds={children.map((c) => c.selfId)}
              >
                <Button
                  size='small'
                  type='link'
                  className='text-xs opacity-0 transition !px-0 group-hover:opacity-100'
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('edit')}
                </Button>
              </RelationsCreatorDropdown>
            )}
          </Text>
        </div>
      </div>
    )
    return (
      <Draggable
        key={objective.objectiveId.toString()}
        draggableId={objective.objectiveId.toString()}
        index={index}
        isDragDisabled={!canUpdate}
      >
        {(provided) => (
          <div ref={provided.innerRef} {...provided.dragHandleProps} {...provided.draggableProps}>
            <Collapse ghost destroyInactivePanel activeKey={activeKeys} onChange={onKeyChange}>
              <Collapse.Panel key={node.id} header={header} showArrow={children.length > 0}>
                <TeamOKRChildren
                  selfId={`${objective.objectiveId}`}
                  type={ResourceType.OBJECTIVE}
                  nodes={children}
                  refresh={refresh}
                  canDelete={canDelete}
                  canUpdate={canUpdate}
                  depth={depth + 1}
                  activeKeys={activeKeys}
                  onKeyChange={onKeyChange}
                />
              </Collapse.Panel>
            </Collapse>
          </div>
        )}
      </Draggable>
    )
  })

  const childPpRelations = nodes.filter((n) =>
    [
      ResourceType.PROJECT,
      ResourceType.PORTFOLIO,
      ResourceType.CUSTOMER,
      ResourceType.PRODUCTION,
      ResourceType.DEVLOP,
    ].includes(n.type)
  ) as PpRelationBase[]
  const table = childPpRelations.length > 0 && (
    <NormalResourceTable
      selfId={selfId}
      type={type}
      dataSource={childPpRelations}
      pageType={ResourceType.TEAM}
      onSuccess={refresh}
      canUpdate={canUpdate}
      canDelete={canDelete}
      showHeader={false}
      size='small'
    />
  )

  return (
    <div className={depth > 0 ? 'ml-24px' : undefined}>
      {table}
      <Droppable droppableId={selfId} type={selfId} isDropDisabled={!canUpdate}>
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {childObjectives}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  )
}

export const TeamOKR: React.FC<TeamOKRProps> = ({ team, departmentId, setProgress, setUsers }) => {
  const { t } = useTranslation()
  const { canRead, canUpdate, canDelete } = getPointPermission(PAGE_RESOURCE_POINTS_ENUM.目标, team.permissions)
  const selfId = team?.selfId

  const history = useHistory()
  const { search } = useLocation()
  const [okrCycles] = useAtom(okrCyclesAtom)
  const [currentOKRCycle] = useAtom(currentOKRCycleAtom)
  const [cycleId, setCycleId] = React.useState<number>(null as any)
  const [activeKeys, setActiveKeys] = React.useState<string[]>([])
  const [expandAll, setExpandAll] = React.useState(false)

  const { data, run, loading } = useRequest<PpRelation[]>(
    {
      url: '/api/relations/getPpRelations',
      params: {
        parentSelfId: selfId,
        departmentId,
        cycleId,
        rootTypes: [2],
        childTypes: [
          ResourceType.PROJECT,
          ResourceType.PORTFOLIO,
          ResourceType.OBJECTIVE,
          ResourceType.CUSTOMER,
          ResourceType.PRODUCTION,
          ResourceType.DEVLOP,
        ],
        fetchType: 1,
      },
    },
    {
      manual: true,
      formatResult: deleteChildren,
    }
  )

  const dataFlatMap = data && keyBy(flatten(data), 'selfId')
  if (data && data.length > 0) {
    let progress = 0
    data.forEach((d) => (progress += d.objective.progress))
    progress = progress / data.length
    setProgress(progress)
  }

  const { run: refresh } = useRequest<PpRelation[]>(
    {
      url: '/api/team/refreshObjectiveByDepartment',
      method: 'post',
      data: {
        departmentId,
      },
    },
    {
      manual: true,
      onSuccess: () => canRead && run(),
    }
  )

  const { run: updateOrder } = useRequest(
    ({ children }) => ({
      url: '/api/project/updatePpRelationsOrder',
      method: 'post',
      data: {
        selfId,
        children,
      },
    }),
    {
      manual: true,
    }
  )

  const searchParsed = queryString.parse(search)
  if (searchParsed.cycleId && cycleId !== Number(searchParsed.cycleId)) {
    setCycleId(Number(searchParsed.cycleId))
  }

  React.useEffect(() => {
    if (currentOKRCycle && !cycleId) {
      history.replace({
        search: queryString.stringify({ cycleId: currentOKRCycle.cycleId, departmentId }),
      })
      setCycleId(currentOKRCycle.cycleId)
    }
  }, [currentOKRCycle])

  React.useEffect(() => {
    if (selfId && cycleId && canRead) {
      run()
    }
  }, [selfId, cycleId])

  React.useEffect(() => {
    setExpandAll(false)
  }, [departmentId])

  const onToggleExpandAll = () => {
    if (!data) {
      return
    }
    const getIds = (data: PpRelation): any[] => {
      return [data.id, flatMapDeep(data.children || [], getIds)]
    }
    setActiveKeys(expandAll ? [] : flatMapDeep(data, getIds))
    setExpandAll((expandAll) => !expandAll)
  }

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination || result.source.index === result.destination.index) return
    if (!dataFlatMap) return
    let sendOrder = []
    if (dataFlatMap[result.type]) {
      sendOrder = reorder(dataFlatMap[result.type].children || [], result.source.index, result.destination.index)
    } else {
      sendOrder = reorder(data, result.source.index, result.destination.index)
    }
    await updateOrder({ children: sendOrder })
    run()
  }

  const children = canRead ? (
    data && data.length === 0 ? (
      <Empty
        description={
          <a
            target='_blank'
            href={`https://okr.agoralab.co/okr/overview?cycle=${cycleId}&selectKey=${departmentId}`}
            rel='noreferrer'
          >
            {t('objective.create')}
          </a>
        }
      />
    ) : (
      <Spin spinning={loading || !selfId} delay={300}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={selfId!} type={selfId} isDropDisabled={!canUpdate}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                <TeamOKRChildren
                  selfId={selfId!}
                  type={2}
                  nodes={data}
                  refresh={run}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                  depth={0}
                  activeKeys={activeKeys}
                  onKeyChange={(keys) => {
                    setActiveKeys(keys as string[])
                  }}
                />
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Spin>
    )
  ) : (
    <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.目标} />
  )

  const onChange = (cycleId: number) => {
    setCycleId(cycleId)
    history.replace({
      search: queryString.stringify({ departmentId, cycleId }),
    })
  }

  const refreshObjectives = () => {
    refresh()
  }

  return (
    <div>
      <div className='mb-32px'>
        {team && <ResourceUsers isShowTip={false} source={team} resourceType={ResourceType.TEAM} setUsers={setUsers} />}
      </div>
      {departmentId !== 1 && (
        <>
          <div className='flex mb-12px justify-between'>
            <div>
              {data && (
                <Button onClick={onToggleExpandAll} icon={expandAll ? <ShrinkOutlined /> : <ExpandAltOutlined />}>
                  {expandAll ? t('collapse all') : t('expand all')}
                </Button>
              )}
            </div>
            <div>
              {t('change quarter')}：
              <Select
                options={okrCycles.map((c) => ({ value: c.cycleId, label: c.name }))}
                value={cycleId}
                onChange={onChange}
              />
              <Button
                className='ml-4'
                type='primary'
                shape='circle'
                onClick={refreshObjectives}
                icon={<ReloadOutlined />}
                disabled={!canUpdate}
              />
            </div>
          </div>
          {selfId && children}
        </>
      )}
    </div>
  )
}
